import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAdminPost, listAdminPosts, savePost } from "@/lib/posts.functions";
import { PostEditorWorkspace, type EditablePost } from "@/components/admin-workspace/PostEditorWorkspace";
import type { ListRow } from "@/components/admin-workspace/model";

export const Route = createFileRoute("/admin/yazi/$id")({
  head: () => ({ meta: [{ title: "Yazıyı düzenle · Yönetim" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: Page,
});

function Page() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchPost = useServerFn(getAdminPost);
  const list = useServerFn(listAdminPosts);
  const save = useServerFn(savePost);
  const { data, isLoading, error } = useQuery({ queryKey: ["admin-post", id], queryFn: () => fetchPost({ data: { id } }) });
  const { data: all } = useQuery({ queryKey: ["admin-posts"], queryFn: () => list() });

  if (isLoading) return <p className="p-10 text-sm text-ink/60">Yükleniyor…</p>;
  if (error || !data) return <p className="p-10 text-sm text-clay">Yazı bulunamadı.</p>;

  const initial: EditablePost = {
    id: data.id, slug: data.slug, title_tr: data.title_tr, title_en: data.title_en,
    excerpt_tr: data.excerpt_tr, excerpt_en: data.excerpt_en, content_tr: data.content_tr, content_en: data.content_en,
    category: data.category, cover_image_url: data.cover_image_url, published: data.published,
    show_on_home: data.show_on_home ?? true, tags: data.tags ?? [], published_at: data.published_at,
    cover_meta: ((data as { cover_meta?: unknown }).cover_meta as EditablePost["cover_meta"]) ?? undefined,
    seo_title: data.seo_title ?? null,
    post_type: (data.post_type as EditablePost["post_type"]) ?? "yazi",
    collection: (data.collection as EditablePost["collection"]) ?? null,
    related_slugs: data.related_slugs ?? [],
    book_id: (data.book_id as EditablePost["book_id"]) ?? null,
    scheduled_at: data.scheduled_at ?? null,
  };

  return (
    <PostEditorWorkspace
      key={data.id}
      initial={initial}
      allPosts={(all?.posts ?? []) as ListRow[]}
      onBack={() => navigate({ to: "/admin/icerik" })}
      onSave={async (p) => {
        const { published_at: _ignored, ...payload } = p;
        const r = await save({ data: payload });
        void qc.invalidateQueries({ queryKey: ["admin-posts"] });
        return r;
      }}
    />
  );
}
